import React, { useState, useRef, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, Stack, useLocalSearchParams, Href } from "expo-router";
import { useForm } from "react-hook-form";
import { Linking, Pressable, ScrollView, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { BackButton } from "@/shared/components/ui/back-button";
import { ScreenContainer } from "@/shared/components/ui/screen-container";

import { useSignup } from "@/features/auth/api/auth";
import { apiFetch } from "@/shared/lib/bff/client";
import { posthog } from "@/shared/lib/posthog";
import { useResendCooldown } from "@/features/auth/hooks/use-resend-cooldown";
import { FormInput } from "@/shared/components/ui/form-input";
import { Button } from "@/shared/components/ui/button";
import { EXTERNAL_LINKS } from "@/shared/constants/links";
import {
  SignupEmailFormData,
  signupEmailSchema,
  SignupCodeFormData,
  signupCodeSchema,
  SignupPasswordFormData,
  signupPasswordSchema,
} from "@/features/auth/schemas/auth";

type WizardData = {
  email: string;
  code: string;
  password: string;
};

function EmailStep({
  defaultEmail,
  error,
  isLoading,
  onNext,
}: {
  defaultEmail: string;
  error?: string | null;
  isLoading: boolean;
  onNext: (email: string) => void;
}) {
  const { control, handleSubmit } = useForm<SignupEmailFormData>({
    resolver: zodResolver(signupEmailSchema),
    defaultValues: { email: defaultEmail },
  });

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Qual o seu e-mail?
      </Text>

      <FormInput
        control={control}
        name="email"
        label="E-mail"
        placeholder="seuemail@exemplo.com"
        keyboardType="email-address"
        autoComplete="email"
      />

      {error && <Text className="text-destructive mb-2 text-sm">{error}</Text>}

      <Button
        size="lg"
        className="mt-4"
        onPress={handleSubmit((data) => onNext(data.email))}
        loading={isLoading}
      >
        Continuar
      </Button>
    </>
  );
}

function CodeStep({
  email,
  error,
  isLoading,
  isSendingCode,
  isOnCooldown,
  secondsLeft,
  onNext,
  onResend,
}: {
  email: string;
  error?: string | null;
  isLoading: boolean;
  isSendingCode: boolean;
  isOnCooldown: boolean;
  secondsLeft: number;
  onNext: (code: string) => void;
  onResend: () => void;
}) {
  const { control, handleSubmit } = useForm<SignupCodeFormData>({
    resolver: zodResolver(signupCodeSchema),
    defaultValues: { code: "" },
  });

  const onSubmitCode = (data: SignupCodeFormData) => {
    onNext(data.code);
  };

  return (
    <>
      <Text className="text-foreground font-display mb-2 text-2xl font-semibold">
        Digite o código que enviamos para o seu e-mail
      </Text>

      <Text className="text-muted-foreground mb-6 text-sm">{email}</Text>

      <FormInput
        control={control}
        name="code"
        label="Código"
        placeholder="000000"
        keyboardType="number-pad"
        autoComplete="one-time-code"
      />

      {error && <Text className="text-destructive mb-2 text-sm">{error}</Text>}

      <Button
        size="lg"
        className="mt-4"
        onPress={handleSubmit(onSubmitCode)}
        loading={isLoading}
      >
        Confirmar
      </Button>

      <Button
        variant="ghost"
        size="lg"
        className="mt-2"
        onPress={onResend}
        loading={isSendingCode}
        disabled={isOnCooldown}
      >
        {isOnCooldown ? `Reenviar código (${secondsLeft}s)` : "Reenviar código"}
      </Button>
    </>
  );
}

function PasswordStep({ onNext }: { onNext: (password: string) => void }) {
  const { control, handleSubmit } = useForm<SignupPasswordFormData>({
    resolver: zodResolver(signupPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Crie sua senha
      </Text>

      <FormInput
        control={control}
        name="password"
        label="Senha"
        placeholder="Sua senha"
        secureTextEntry
        showPasswordToggle
        autoComplete="password-new"
      />

      <FormInput
        control={control}
        name="confirmPassword"
        label="Confirmar senha"
        placeholder="Confirme sua senha"
        secureTextEntry
        showPasswordToggle
        autoComplete="password-new"
      />

      <Button
        className="mt-4"
        size="lg"
        onPress={handleSubmit((data) => onNext(data.password))}
      >
        Continuar
      </Button>
    </>
  );
}

function CheckboxItem({
  checked,
  onToggle,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const [primaryForeground] = useCSSVariable([
    "--color-primary-foreground",
  ]) as [string];
  return (
    <Pressable
      onPress={onToggle}
      className="mb-4 flex-row items-start gap-3"
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
    >
      <View
        className={`mt-0.5 h-5 w-5 items-center justify-center rounded border ${
          checked ? "bg-primary border-primary" : "border-border"
        }`}
      >
        {checked && (
          <Ionicons name="checkmark" size={14} color={primaryForeground} />
        )}
      </View>
      <View className="flex-1">{children}</View>
    </Pressable>
  );
}

function TermsStep({
  onSubmit,
  isLoading,
  error,
}: {
  onSubmit: (optInMarketing: boolean) => void;
  isLoading: boolean;
  error?: string | null;
}) {
  const [isAdult, setIsAdult] = useState(false);
  const [isSaphic, setIsSaphic] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [optInMarketing, setOptInMarketing] = useState(false);

  const allChecked = isAdult && isSaphic && agreedToTerms;

  return (
    <>
      <Text className="text-foreground font-display mb-2 text-2xl font-semibold">
        Antes de continuar
      </Text>

      <Text className="text-muted-foreground mb-8 text-sm">
        Por favor, confirme as informações abaixo para criar sua conta.
      </Text>

      <CheckboxItem checked={isAdult} onToggle={() => setIsAdult((v) => !v)}>
        <Text className="text-foreground text-sm">
          Confirmo que tenho 18 anos ou mais
        </Text>
      </CheckboxItem>

      <CheckboxItem checked={isSaphic} onToggle={() => setIsSaphic((v) => !v)}>
        <Text className="text-foreground text-sm">
          Confirmo que sou uma mulher interessada em eventos para mulheres
          sáficas
        </Text>
      </CheckboxItem>

      <CheckboxItem
        checked={agreedToTerms}
        onToggle={() => setAgreedToTerms((v) => !v)}
      >
        <Text className="text-foreground text-sm">
          Li e concordo com os{" "}
          <Text
            className="text-primary"
            onPress={() => Linking.openURL(EXTERNAL_LINKS.termsOfUse)}
          >
            Termos de Uso
          </Text>{" "}
          e{" "}
          <Text
            className="text-primary"
            onPress={() => Linking.openURL(EXTERNAL_LINKS.privacyPolicy)}
          >
            Política de Privacidade
          </Text>
        </Text>
      </CheckboxItem>

      <CheckboxItem
        checked={optInMarketing}
        onToggle={() => setOptInMarketing((v) => !v)}
      >
        <Text className="text-foreground text-sm">
          Quero receber novidades e comunicações da Lezz por e-mail (opcional)
        </Text>
      </CheckboxItem>

      {error && <Text className="text-destructive mb-2 text-sm">{error}</Text>}

      <Button
        className="mt-4"
        size="lg"
        onPress={() => onSubmit(optInMarketing)}
        loading={isLoading}
        disabled={!allChecked}
      >
        Criar conta
      </Button>
    </>
  );
}

export default function SignupScreen() {
  const router = useRouter();
  const signup = useSignup();
  const { redirect } = useLocalSearchParams<{ redirect?: string }>();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  const wizardData = useRef<Partial<WizardData>>({});
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const { secondsLeft, isOnCooldown, startCooldown } = useResendCooldown();

  const handleBack = useCallback(() => {
    if (currentStep === 1) {
      router.back();
    } else {
      setCurrentStep((s) => (s - 1) as 1 | 2 | 3 | 4);
    }
  }, [currentStep, router]);

  const sendCode = useCallback(
    async (email: string) => {
      setIsSendingCode(true);
      try {
        await apiFetch("/api/auth/send-code", {
          method: "POST",
          body: JSON.stringify({ email }),
        });
        startCooldown();
        return true;
      } catch (err) {
        setEmailError(
          err instanceof Error ? err.message : "Erro ao enviar código"
        );
        return false;
      } finally {
        setIsSendingCode(false);
      }
    },
    [startCooldown]
  );

  const handleEmailNext = useCallback(
    async (email: string) => {
      wizardData.current.email = email;
      setEmailError(null);
      const ok = await sendCode(email);
      if (ok) {
        posthog?.capture("signup_email_submitted");
        setCurrentStep(2);
      }
    },
    [sendCode]
  );

  const handleCodeNext = useCallback(async (code: string) => {
    setIsVerifyingCode(true);
    setCodeError(null);
    try {
      await apiFetch("/api/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ email: wizardData.current.email, code }),
      });
      wizardData.current.code = code;
      posthog?.capture("signup_code_verified");
      setCurrentStep(3);
    } catch (err) {
      setCodeError(
        err instanceof Error ? err.message : "Código inválido ou expirado"
      );
    } finally {
      setIsVerifyingCode(false);
    }
  }, []);

  const handlePasswordNext = useCallback((password: string) => {
    wizardData.current.password = password;
    posthog?.capture("signup_password_set");
    setCurrentStep(4);
  }, []);

  return (
    <>
      <Stack.Screen options={{ title: "Sign Up", headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <ScrollView
          className="flex-1"
          contentContainerClassName="grow"
          keyboardShouldPersistTaps="handled"
        >
          <View className="p-6">
            <BackButton
              className="mt-8 mb-8"
              onPress={handleBack}
              accessibilityLabel="Voltar"
            />

            <Text className="text-muted-foreground mb-2 text-sm">
              Vamos iniciar seu cadastro
            </Text>

            {currentStep === 1 && (
              <EmailStep
                defaultEmail={wizardData.current.email ?? ""}
                error={emailError}
                isLoading={isSendingCode}
                onNext={handleEmailNext}
              />
            )}
            {currentStep === 2 && (
              <CodeStep
                email={wizardData.current.email!}
                error={codeError}
                isLoading={isVerifyingCode}
                isSendingCode={isSendingCode}
                isOnCooldown={isOnCooldown}
                secondsLeft={secondsLeft}
                onNext={handleCodeNext}
                onResend={() => {
                  posthog?.capture("signup_code_resent");
                  setCodeError(null);
                  sendCode(wizardData.current.email!);
                }}
              />
            )}
            {currentStep === 3 && <PasswordStep onNext={handlePasswordNext} />}
            {currentStep === 4 && (
              <TermsStep
                onSubmit={(optInMarketing) => {
                  setSignupError(null);
                  signup.mutate(
                    {
                      email: wizardData.current.email!,
                      password: wizardData.current.password!,
                      confirmPassword: wizardData.current.password!,
                      optInMarketing,
                    },
                    {
                      onSuccess: () => {
                        const redirectPath = redirect
                          ? `?${encodeURIComponent(redirect)}`
                          : "";
                        router.push(`/complete-profile${redirectPath}` as Href);
                      },
                      onError: (error) => {
                        if (
                          error.message
                            .toLowerCase()
                            .includes("already registered")
                        ) {
                          setEmailError(
                            "Este e-mail já está cadastrado. Tente fazer login."
                          );
                          setCurrentStep(1);
                        } else {
                          setSignupError(
                            "Erro ao criar conta. Tente novamente."
                          );
                        }
                      },
                    }
                  );
                }}
                isLoading={signup.isPending}
                error={signupError}
              />
            )}
          </View>
        </ScrollView>
      </ScreenContainer>
    </>
  );
}
